import { createSelector } from '@reduxjs/toolkit';

import {
    Account,
    AccountInfo,
    OfferResponseStatus,
    Partner,
    PurchaseEligibility,
    ServiceLocation,
    ShoppingCart,
    SubmitShoppingCartResponse,
    UserDetails,
} from 'app/api/@generated/backend';

import { HouseType } from 'app/pages/order/model/house-type';
import { RootState } from 'app/redux/state/root-state';
import { OrderService } from 'app/pages/order/services/order-service';
import { PartnerUtils } from 'app/pages/order/utils/partner-utils';

import { OrderRequest } from './model/order-request';
import { OrderStep } from './model/order-step';

export const selectAccounts = (state: RootState): Account[] => state.order.accounts;

export const selectPurchaseEligibility = (state: RootState): PurchaseEligibility | undefined =>
    state.order.purchaseEligibility;

export const selectSelectedAccount = (state: RootState): Account | undefined => state.order.selectedAccount;

export const selectSelectedAccountOrThrow = (state: RootState): Account =>
    selectSelectedAccount(state) ||
    (() => {
        throw new Error('Invalid state. Account must be selected.');
    })();

export const selectServiceLocationsOrThrow = (state: RootState): ServiceLocation[] =>
    selectSelectedAccountInfoOrThrow(state).serviceLocations;

export const selectServiceLocations = (state: RootState): ServiceLocation[] | undefined =>
    selectSelectedAccountInfo(state)?.serviceLocations;

export const selectSelectedServiceLocationId = (state: RootState): number | undefined =>
    state.order.selectedServiceLocationId;

export const selectSelectedServiceLocation = (state: RootState): ServiceLocation | undefined =>
    selectServiceLocations(state)?.find(
        (serviceLocation) => serviceLocation.siteId === selectSelectedServiceLocationId(state),
    );

export const selectSelectedServiceLocationOrThrow = (state: RootState): ServiceLocation =>
    selectSelectedServiceLocation(state) ||
    (() => {
        throw new Error('Invalid state. Service location must be present.');
    })();

export const selectPartner = (state: RootState): Partner | undefined => {
    const { partnerId } = selectSelectedAccountOrThrow(state);
    const partners = selectPartners(state);

    return PartnerUtils.getPartnerById(partners, partnerId);
};

export const selectSelectedUserDetails = (state: RootState): UserDetails | undefined =>
    selectSelectedAccountInfo(state)?.userDetails;

export const selectSelectedUserDetailsOrThrow = (state: RootState): UserDetails =>
    selectSelectedAccountInfoOrThrow(state).userDetails;

const selectSelectedAccountInfo = (state: RootState): AccountInfo | undefined => state.order.accountInfo;

const selectSelectedAccountInfoOrThrow = (state: RootState): AccountInfo =>
    selectSelectedAccountInfo(state) ||
    (() => {
        throw new Error('Invalid state account info must be present');
    })();

export const selectSelectedHouseType = (state: RootState): HouseType => state.order.selectedHouseType;

export const selectIsExtraUnitNeeded = (state: RootState): boolean => state.order.isExtraUnitNeeded;

export const selectIsPurchasingSingleUnit = (state: RootState): boolean => state.order.isPurchasingSingleUnit;

export const selectSelectedAmountOfUnitsToPurchase = createSelector(
    [selectIsPurchasingSingleUnit, selectIsExtraUnitNeeded, selectSelectedHouseType],
    OrderService.getAmountOfWifiUnitsToBePurchased,
);

export const selectShoppingCart = (state: RootState): ShoppingCart | undefined => state.order.shoppingCart;

export const selectShoppingCartOrThrow = (state: RootState): ShoppingCart =>
    selectShoppingCart(state) ||
    (() => {
        throw new Error('Invalid state. Shopping cart must be present.');
    })();

export const selectPartners = (state: RootState): Partner[] => state.order.partners;

export const selectReachedStep = (state: RootState): OrderStep => state.order.reachedStep;

export const selectOrderRequests = (state: RootState): OrderRequest[] => state.order.orderRequests;

export const selectBroadbandOfferStatus = (state: RootState): OfferResponseStatus | undefined =>
    state.order.broadbandOffer?.status;

const selectBroadbandOfferId = (state: RootState): string | undefined => state.order.broadbandOffer?.response?.offerId;

export const selectBroadbandOfferIdOrThrow = (state: RootState): string =>
    selectBroadbandOfferId(state) ||
    (() => {
        throw new Error('Invalid state. Broadband offer id must be present.');
    })();

export const selectHasCustomerDebt = (state: RootState): boolean => !!selectPurchaseEligibility(state)?.isInDebt;

export const selectOrderConfirmation = (state: RootState): SubmitShoppingCartResponse | undefined =>
    state.order.orderConfirmation;

export const selectIsShopEnabled = (state: RootState): boolean | undefined => state.order.shopStatus?.enabled;
